import { useState, useEffect } from 'react';
import postGetConditionSources from '../functions/API/postGetConditionSources.js';
import postUpdateStep from '../functions/API/postUpdateStep.js';
import { toast } from '../lib/toast.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Plus, X, GitBranch, Loader2 } from 'lucide-react';

// Operators offered per field type, kept logical for each. Multi-selects use the
// multi-value "includes any/none of" operators; single-value types use "is/is not".
const OPERATORS_BY_TYPE = {
    BOOLEAN: [
        { value: 'equals', label: 'is' },
        { value: 'not_equals', label: 'is not' },
    ],
    DROPDOWN: [
        { value: 'equals', label: 'is' },
        { value: 'not_equals', label: 'is not' },
        { value: 'in', label: 'is one of' },
        { value: 'not_in', label: 'is not one of' },
        { value: 'is_empty', label: 'is empty' },
        { value: 'is_not_empty', label: 'is not empty' },
    ],
    MULTI_SELECT: [
        { value: 'in', label: 'includes any of' },
        { value: 'not_in', label: 'includes none of' },
        { value: 'is_empty', label: 'is empty' },
        { value: 'is_not_empty', label: 'is not empty' },
    ],
};

const ALL_OPERATOR_LABELS = {
    equals: 'is', not_equals: 'is not',
    in: 'is one of', not_in: 'is not one of',
    is_empty: 'is empty', is_not_empty: 'is not empty',
};

// "is one of" / "is not one of" accept multiple values (matched against the
// selected options of a multi-select, or against a single dropdown value).
const isMultiValueOperator = (operator) => operator === 'in' || operator === 'not_in';

// The appropriate empty value for a given operator: null for empty checks,
// [] for multi-value operators, '' for single-value operators.
function emptyValueForOperator(operator) {
    if (operator === 'is_empty' || operator === 'is_not_empty') return null;
    if (isMultiValueOperator(operator)) return [];
    return '';
}

// Coerce an existing value into the shape the given operator expects, so
// switching operators (or loading legacy single-value conditions) is lossless.
function normalizeValueForOperator(operator, value) {
    if (operator === 'is_empty' || operator === 'is_not_empty') return null;
    if (isMultiValueOperator(operator)) {
        if (Array.isArray(value)) return value;
        return value === null || value === undefined || value === '' ? [] : [value];
    }
    return Array.isArray(value) ? (value[0] ?? '') : (value ?? '');
}

export default function ConditionForm({ setVisible, refresh, refreshValue, taskFlowId, targetStep, editMode = false }) {
    const [conditionSources, setConditionSources] = useState([]);
    const [loadingSources, setLoadingSources] = useState(true);
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [conditions, setConditions] = useState(() => {
        if (editMode && targetStep.conditional_logic && targetStep.conditional_logic.length > 0) {
            return targetStep.conditional_logic.map(c => {
                const operator = c.operator || 'equals';
                return {
                    source_step_id: c.source_step_id?.toString() || '',
                    source_field_id: c.source_field_id?.toString() || '',
                    operator,
                    value: normalizeValueForOperator(operator, c.value),
                };
            });
        }
        return [{ source_step_id: '', source_field_id: '', operator: 'equals', value: '' }];
    });

    useEffect(() => {
        fetchConditionSources();
    }, []);

    async function fetchConditionSources() {
        setLoadingSources(true);
        try {
            const response = await postGetConditionSources(taskFlowId, targetStep.id);
            if (response.response.ok && response.data?.sources) {
                setConditionSources(response.data.sources);
            }
        } catch (error) {
            console.error('Error loading condition sources:', error);
        }
        setLoadingSources(false);
    }

    function addCondition() {
        setConditions([...conditions, { source_step_id: '', source_field_id: '', operator: 'equals', value: '' }]);
    }

    function removeCondition(index) {
        setConditions(conditions.filter((_, i) => i !== index));
    }

    function updateCondition(index, field, value) {
        const updated = [...conditions];
        updated[index] = { ...updated[index], [field]: value };
        if (field === 'source_step_id') {
            updated[index].source_field_id = '';
            updated[index].value = emptyValueForOperator(updated[index].operator);
        }
        if (field === 'source_field_id') {
            // Drop to a valid operator if the new field disallows the current one
            // (e.g. equals -> is one of when switching to a multi-select).
            const newField = getFieldForCondition(updated[index]);
            const allowed = getAllowedOperators(newField).map(op => op.value);
            if (!allowed.includes(updated[index].operator)) {
                updated[index].operator = allowed[0] || 'equals';
            }
            updated[index].value = emptyValueForOperator(updated[index].operator);
        }
        if (field === 'operator') {
            // Keep the value where it makes sense when switching operators
            // (e.g. equals <-> is one of), otherwise reset to the right shape.
            updated[index].value = normalizeValueForOperator(value, updated[index].value);
        }
        setConditions(updated);
    }

    // Toggle a single option on/off for a multi-value ("is one of") condition.
    function toggleValueOption(index, optionValue) {
        const updated = [...conditions];
        const current = Array.isArray(updated[index].value) ? updated[index].value : [];
        updated[index] = {
            ...updated[index],
            value: current.includes(optionValue)
                ? current.filter(v => v !== optionValue)
                : [...current, optionValue],
        };
        setConditions(updated);
    }

    function getFieldForCondition(condition) {
        const source = conditionSources.find(s => s.step_id == condition.source_step_id);
        if (!source) return null;
        return source.fields.find(f => f.field_id == condition.source_field_id) || null;
    }

    // Operators valid for a given source field type. The currently selected
    // operator is always kept so legacy conditions never blank out.
    function getAllowedOperators(field, currentOperator) {
        let operators = OPERATORS_BY_TYPE[field?.field_type] || OPERATORS_BY_TYPE.DROPDOWN;
        if (currentOperator && !operators.some(op => op.value === currentOperator)) {
            operators = [...operators, { value: currentOperator, label: ALL_OPERATOR_LABELS[currentOperator] || currentOperator }];
        }
        return operators;
    }

    function getFieldOptionsForCondition(condition) {
        const field = getFieldForCondition(condition);
        if (!field || !field.validation_rules) return [];
        if (field.field_type === 'BOOLEAN') {
            return [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }];
        }
        const rules = typeof field.validation_rules === 'string'
            ? JSON.parse(field.validation_rules)
            : field.validation_rules;
        return rules.options || [];
    }

    async function handleSave() {
        const validConditions = conditions.filter(c => {
            if (!c.source_step_id || !c.source_field_id || !c.operator) return false;
            // "is one of" / "is not one of" need at least one value selected
            if (isMultiValueOperator(c.operator)) {
                return Array.isArray(c.value) && c.value.length > 0;
            }
            return true;
        }).map(c => ({
            source_step_id: parseInt(c.source_step_id),
            source_field_id: parseInt(c.source_field_id),
            operator: c.operator,
            value: c.value ?? null,
        }));

        if (validConditions.length === 0) {
            toast.error('Please configure at least one complete condition.');
            return;
        }

        setSaving(true);
        try {
            const finalConditions = editMode
                ? validConditions
                : [...(targetStep.conditional_logic || []), ...validConditions];

            const body = {
                id: targetStep.id,
                task_flow_id: taskFlowId,
                name: targetStep.name,
                description: targetStep.description,
                actor_type: targetStep.actor_type,
                step_type: targetStep.step_type,
                step_config: targetStep.step_config,
                is_required: targetStep.is_required,
                requires_previous_step_complete: targetStep.requires_previous_step_complete,
                blocks_tenant_until_complete: targetStep.blocks_tenant_until_complete,
                help_url: targetStep.help_url || null,
                help_text: targetStep.help_text || null,
                conditional_logic: finalConditions,
                auto_outcome_rules: targetStep.auto_outcome_rules || null,
            };

            const response = await postUpdateStep(body);
            if (response.response.ok) {
                toast.success(editMode ? 'Conditions updated.' : 'Visibility condition added.');
                refresh(refreshValue + 1);
                setVisible(false);
            } else {
                toast.error('Failed to save condition.');
            }
        } catch {
            toast.error('Failed to save condition.');
        }
        setSaving(false);
    }

    async function handleRemoveAll() {
        setRemoving(true);
        try {
            const body = {
                id: targetStep.id,
                task_flow_id: taskFlowId,
                name: targetStep.name,
                description: targetStep.description,
                actor_type: targetStep.actor_type,
                step_type: targetStep.step_type,
                step_config: targetStep.step_config,
                is_required: targetStep.is_required,
                requires_previous_step_complete: targetStep.requires_previous_step_complete,
                blocks_tenant_until_complete: targetStep.blocks_tenant_until_complete,
                help_url: targetStep.help_url || null,
                help_text: targetStep.help_text || null,
                conditional_logic: null,
                auto_outcome_rules: targetStep.auto_outcome_rules || null,
            };

            const response = await postUpdateStep(body);
            if (response.response.ok) {
                toast.success('All conditions removed.');
                refresh(refreshValue + 1);
                setVisible(false);
            } else {
                toast.error('Failed to remove conditions.');
            }
        } catch {
            toast.error('Failed to remove conditions.');
        }
        setRemoving(false);
    }

    if (loadingSources) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading available fields...</span>
            </div>
        );
    }

    if (conditionSources.length === 0) {
        return (
            <div className="py-8 text-center">
                <GitBranch className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">No eligible fields</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Prior steps need dropdown, multi-select, or boolean fields to create visibility conditions.
                </p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setVisible(false)}>
                    Close
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
                Add conditions to control when <span className="font-medium text-gray-700 dark:text-gray-300">{targetStep.name}</span> is visible.
                All conditions use AND logic. Conditions can only use Yes/No, dropdown and multi-select fields.
            </p>

            <div className="space-y-2">
                {conditions.map((condition, index) => {
                    const sourceStep = conditionSources.find(s => s.step_id == condition.source_step_id);
                    const sourceFields = sourceStep?.fields || [];
                    const fieldOptions = getFieldOptionsForCondition(condition);
                    const allowedOperators = getAllowedOperators(getFieldForCondition(condition), condition.operator);
                    const needsValue = condition.operator !== 'is_empty' && condition.operator !== 'is_not_empty';

                    return (
                        <div key={index} className="p-2.5 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                                <span className="text-xs font-medium text-purple-700 mt-1">
                                    {index === 0 ? 'Show this step when...' : 'AND...'}
                                </span>
                                {conditions.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeCondition(index)}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                                <Select
                                    value={condition.source_step_id?.toString() || ''}
                                    onValueChange={(val) => updateCondition(index, 'source_step_id', val)}
                                >
                                    <SelectTrigger className="bg-white dark:bg-zinc-800 h-8 text-xs">
                                        <SelectValue placeholder="Select step..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {conditionSources.map(source => (
                                            <SelectItem key={source.step_id} value={source.step_id.toString()}>
                                                Step {source.step_position + 1}: {source.step_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                {condition.source_step_id && (
                                    <Select
                                        value={condition.source_field_id?.toString() || ''}
                                        onValueChange={(val) => updateCondition(index, 'source_field_id', val)}
                                    >
                                        <SelectTrigger className="bg-white dark:bg-zinc-800 h-8 text-xs">
                                            <SelectValue placeholder="Select field..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {sourceFields.map(field => (
                                                <SelectItem key={field.field_id} value={field.field_id.toString()}>
                                                    {field.field_name} ({field.field_type})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}

                                {condition.source_field_id && (
                                    <div className="flex gap-2">
                                        <Select
                                            value={condition.operator || 'equals'}
                                            onValueChange={(val) => updateCondition(index, 'operator', val)}
                                        >
                                            <SelectTrigger className="bg-white dark:bg-zinc-800 h-8 text-xs w-[140px] shrink-0">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {allowedOperators.map(op => (
                                                    <SelectItem key={op.value} value={op.value}>
                                                        {op.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>

                                        {needsValue && fieldOptions.length > 0 && isMultiValueOperator(condition.operator) && (
                                            <div className="flex flex-wrap gap-1 flex-1">
                                                {fieldOptions.map(opt => {
                                                    const selected = Array.isArray(condition.value) && condition.value.includes(opt.value);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={opt.value}
                                                            onClick={() => toggleValueOption(index, opt.value)}
                                                            aria-pressed={selected}
                                                            className={`px-2 h-8 rounded-md text-xs border transition-colors ${
                                                                selected
                                                                    ? 'bg-purple-600 border-purple-600 text-white'
                                                                    : 'bg-white dark:bg-zinc-800 border-gray-300 dark:border-zinc-600 text-gray-700 dark:text-gray-200 hover:border-purple-400'
                                                            }`}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {needsValue && fieldOptions.length > 0 && !isMultiValueOperator(condition.operator) && (
                                            <Select
                                                value={condition.value?.toString() || ''}
                                                onValueChange={(val) => updateCondition(index, 'value', val)}
                                            >
                                                <SelectTrigger className="bg-white dark:bg-zinc-800 h-8 text-xs flex-1">
                                                    <SelectValue placeholder="Select value..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {fieldOptions.map(opt => (
                                                        <SelectItem key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}

                                        {needsValue && fieldOptions.length === 0 && (
                                            <input
                                                type="text"
                                                value={condition.value || ''}
                                                onChange={(e) => updateCondition(index, 'value', e.target.value)}
                                                placeholder="Value"
                                                className="flex-1 rounded-md border-0 py-1 px-2 text-xs text-gray-900 dark:text-white dark:bg-zinc-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 focus:ring-2 focus:ring-inset focus:ring-purple-600"
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {conditions.length > 1 && (
                    <p className="text-xs text-purple-600 font-medium">All conditions must be met (AND logic)</p>
                )}
            </div>

            <Button type="button" size="sm" variant="outline" onClick={addCondition}>
                <Plus className="h-3 w-3 mr-1" />
                Add another condition
            </Button>

            <div className="flex justify-between gap-2 pt-4 border-t">
                {editMode ? (
                    <Button variant="destructive" size="sm" onClick={handleRemoveAll} disabled={saving || removing}>
                        {removing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Remove All Conditions
                    </Button>
                ) : <div />}
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setVisible(false)} disabled={saving || removing}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving || removing}>
                        {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        {editMode ? 'Update Conditions' : 'Save Condition'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
